import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { GoalsApiService, GoalDto, GoalTypeDto, CustomVariableDto, CustomVariableValueDto } from '../../../services/goals-api.service';
import { AuthService, SessionUser } from '../../../services/auth.service';
import { firstValueFrom, Subscription } from 'rxjs';
import { GoalView } from '../goal.types';
import { CustomSelectComponent, CustomSelectOption } from '../../shared/custom-select/custom-select.component';

/**
 * Tipi di goal valutati sui raw data di `dcr.ads_data_*`: il Type coincide (case-insensitive)
 * con la colonna AdType, vedi df_daily_export_goals in Dataform.
 */
export const ADS_GOAL_TYPES = ['impression', 'click', 'dem'] as const;

/**
 * Campi di `dcr.ads_data_*` su cui una regola Ads può fare match. La Rule salvata è un oggetto
 * JSON con queste chiavi ({"CampaignID": "<regexp>"}); la procedura mette in AND le chiavi
 * valorizzate, il builder ne usa una sola.
 */
export const ADS_GOAL_FIELDS = ['CampaignID', 'PlacementID', 'SiteID'] as const;

export function isAdsGoalType(type: string | null | undefined): boolean {
  return !!type && (ADS_GOAL_TYPES as readonly string[]).includes(type.toLowerCase());
}

@Component({
  selector: 'app-goals-form',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslateModule, CustomSelectComponent],
  templateUrl: './goals-form.component.html',
  styleUrls: ['../goals.component.scss']
})
export class GoalsFormComponent implements OnInit, OnDestroy {
  @Input() goalTypes: GoalTypeDto[] = [];
  @Input() customVariables: CustomVariableDto[] = [];
  private _hasGlobalDataScope = false;
  get hasGlobalDataScope(): boolean { return this._hasGlobalDataScope; }
  @Input() set hasGlobalDataScope(value: boolean) {
    this._hasGlobalDataScope = value;
    const ctrl = this.goalForm?.get('ownerEntitySelect');
    if (value) {
      ctrl?.setValidators([Validators.required]);
    } else {
      ctrl?.clearValidators();
    }
    ctrl?.updateValueAndValidity({ emitEvent: false });
  }
  @Input() ownerEntityOptions: { value: string; label: string; firstPartyGroupID?: string | null }[] = [];
  // Advanced (Console/admin) mode: unlocks the full set of goal types and the raw Rule editor.
  // The user-facing page keeps the simplified experience (URL + custom-variable-list only).
  @Input() advanced = false;
  /** Emette l'ID del goal se il salvataggio ha avviato un'estrazione, altrimenti null. */
  @Output() saved = new EventEmitter<string | null>();
  @Output() cancelled = new EventEmitter<void>();

  formMode: 'create' | 'edit' = 'create';
  formTitleKey = 'goals.form.createTitle';
  goalForm: FormGroup;
  saving = false;
  selectedGoalType: GoalTypeDto | null = null;
  customVarValues: string[] = [];
  urlValues: string[] = [];
  adsValues: string[] = [];
  loadingValues = false;
  /** Inline error shown under the value input (e.g. malformed pattern with the "regexp" operator). */
  valueError: string | null = null;
  private currentUser: SessionUser | null = null;
  /** Rule con cui si è aperto l'edit: serve a non ri-estrarre se la regola non è cambiata. */
  private originalRule: string | null = null;
  private subscriptions = new Subscription();

  get goalTypeOptions(): CustomSelectOption[] {
    // Advanced mode exposes every backend goal type (url, path, custom_data, …);
    // the simplified user mode is limited to the guided builders.
    return this.goalTypes
      .filter(type => this.advanced || type.Type === 'customvariablelist' || type.Type === 'url' || isAdsGoalType(type.Type))
      .map(type => ({
        value: type.Type,
        label: type.TypeName
      }));
  }

  /** A type that uses the raw Rule editor (i.e. not one of the guided builders). */
  get isGenericRuleType(): boolean {
    const t = this.selectedGoalType?.Type;
    return !!t && t !== 'url' && t !== 'customvariablelist' && !isAdsGoalType(t);
  }

  /** Goal su `dcr.ads_data_*` (impression, click, dem): builder campo + operatore + valori. */
  get isAdsType(): boolean {
    return isAdsGoalType(this.selectedGoalType?.Type);
  }

  get adsFieldOptions(): CustomSelectOption[] {
    return ADS_GOAL_FIELDS.map(field => ({ value: field, label: field }));
  }

  get customVarOperatorOptions(): CustomSelectOption[] {
    return [
      { value: 'contains', label: 'Contains' },
      { value: 'equals', label: 'Equals to' },
      { value: 'startswith', label: 'Start with' },
      { value: 'endswith', label: 'End with' },
      { value: 'isnotempty', label: 'Is not empty' },
      // Raw pattern authored by the user: passa a buildRegExpForOperator senza escape.
      // Gli altri operatori trattano il valore come letterale e quindi lo escapano
      // (un "(a|b)" scritto a mano diventerebbe "\(a\|b\)" e in BigQuery non matcherebbe).
      { value: 'regexp', label: 'Matches (RegExp)' }
    ];
  }

  get urlOperatorOptions(): CustomSelectOption[] {
    return [
      { value: 'contains', label: 'Contains' },
      { value: 'equals', label: 'Equals to' },
      // Vedi customVarOperatorOptions.
      { value: 'regexp', label: 'Matches (RegExp)' }
    ];
  }

  /** True when the currently selected operator makes the value a raw (un-escaped) pattern. */
  get isRawRegExpOperator(): boolean {
    return this.selectedOperator === 'regexp';
  }

  /** True when the operator matches on its own (`isnotempty`), so no value has to be entered. */
  get isNoValueOperator(): boolean {
    return this.selectedOperator === 'isnotempty';
  }

  /**
   * Metacaratteri regex che con un operatore letterale vengono escapati e che in un URL
   * o in un valore reale non compaiono quasi mai: se ci sono, l'utente stava scrivendo
   * una regexp e ha scelto l'operatore sbagliato.
   * Volutamente più stretto dell'escape di buildRegExpForOperator: `.`, `?` e `+` sono
   * legittimi in un URL (domini, query string) e come segnale darebbero solo rumore.
   */
  private static readonly REGEX_METACHARS = /[*^${}()|[\]\\]/;

  /** I valori inseriti per il tipo di regola attualmente selezionato. */
  private get activeValues(): string[] {
    const type = this.selectedGoalType?.Type;
    if (type === 'customvariablelist') {
      return this.customVarValues;
    }
    if (type === 'url') {
      return this.urlValues;
    }
    if (isAdsGoalType(type)) {
      return this.adsValues;
    }
    return [];
  }

  /**
   * Il pattern che verrà effettivamente salvato (per customvariablelist è il campo Value
   * dentro la Rule JSON). Mostrarlo è l'unico modo per accorgersi che un operatore
   * letterale ha escapato una regexp scritta a mano: il risultato resta una regexp valida
   * — il check BigQuery lato API la accetta — solo con un significato diverso.
   */
  get compiledPattern(): string {
    const operator = this.selectedOperator;
    if (!operator) {
      return '';
    }
    const values = this.isNoValueOperator ? [''] : this.activeValues;
    if (!values.length) {
      return '';
    }
    return this.buildCombinedRegExp(values, operator);
  }

  /** True quando l'operatore tratta i valori come letterali ma uno di essi sembra una regexp. */
  get literalValuesLookLikeRegExp(): boolean {
    if (this.isRawRegExpOperator || this.isNoValueOperator || !this.selectedOperator) {
      return false;
    }
    return this.activeValues.some(value => GoalsFormComponent.REGEX_METACHARS.test(value));
  }

  private get selectedOperator(): string {
    const type = this.selectedGoalType?.Type;
    if (type === 'customvariablelist') {
      return this.goalForm.get('customVarOperator')?.value || '';
    }
    if (type === 'url') {
      return this.goalForm.get('urlOperator')?.value || '';
    }
    if (isAdsGoalType(type)) {
      return this.goalForm.get('adsOperator')?.value || '';
    }
    return '';
  }

  get customVarNameOptions(): CustomSelectOption[] {
    return this.customVariables.map(variable => ({
      value: variable.Name,
      label: variable.Name === '__ANY__' ? 'Any' : variable.Name
    }));
  }


  constructor(
    private goalsApi: GoalsApiService,
    private fb: FormBuilder,
    private translate: TranslateService,
    private authService: AuthService
  ) {
    this.goalForm = this.fb.group({
      id: [null],
      description: ['', Validators.required],
      type: ['', Validators.required],
      rule: [''],
      isRegExp: [false],
      showInDashboard: [false],
      customVarName: [''],
      customVarValue: [''],
      customVarOperator: [''],
      urlValue: [''],
      urlOperator: [''],
      adsField: [''],
      adsOperator: [''],
      entityType: [''],
      entityId: [''],
      status: [1],
      ownerEntitySelect: ['']
    });

    this.goalForm.get('ownerEntitySelect')?.valueChanges.subscribe((id: string) => {
      if (id) {
        this.goalForm.patchValue({
          entityId: id,
          entityType: 'publisher'
        }, { emitEvent: false });
      }
    });

    this.goalForm.get('customVarOperator')?.valueChanges.subscribe(() => this.valueError = null);
    this.goalForm.get('urlOperator')?.valueChanges.subscribe(() => this.valueError = null);
    this.goalForm.get('adsOperator')?.valueChanges.subscribe(() => this.valueError = null);

    this.goalForm.get('type')?.valueChanges.subscribe(value => {
      this.valueError = null;
      const previousWasAds = this.isAdsType;
      this.selectedGoalType = this.goalTypes.find(type => type.Type === value) ?? null;
      if (this.selectedGoalType?.Type !== 'customvariablelist') {
        this.customVarValues = [];
      }
      if (this.selectedGoalType?.Type !== 'url') {
        this.urlValues = [];
      }
      // Passare da impression a click (o dem) tiene campo, operatore e valori: la regola
      // è la stessa, cambia solo l'AdType su cui viene valutata.
      if (!this.isAdsType || !previousWasAds) {
        this.adsValues = [];
      }
      this.updateValidators();
    });
  }

  ngOnInit(): void {
    this.subscriptions.add(
      this.authService.currentUser$.subscribe(user => {
        this.currentUser = user;
      })
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  openCreate(): void {
    this.formMode = 'create';
    this.formTitleKey = 'goals.form.createTitle';
    this.goalForm.reset({
      description: '',
      type: '',
      rule: '',
      isRegExp: false,
      showInDashboard: false,
      customVarName: '',
      customVarValue: '',
      customVarOperator: '',
      urlValue: '',
      urlOperator: '',
      adsField: '',
      adsOperator: '',
      entityType: '',
      entityId: '',
      status: 1,
      ownerEntitySelect: ''
    });
    this.selectedGoalType = null;
    this.customVarValues = [];
    this.urlValues = [];
    this.adsValues = [];
    this.valueError = null;
    this.originalRule = null;
  }

  openEdit(item: GoalDto | GoalView): void {
    this.formMode = 'edit';
    this.formTitleKey = 'goals.form.editTitle';
    this.valueError = null;

    const type = 'Type' in item ? item.Type : item.type;
    this.selectedGoalType = this.goalTypes.find(t => t.Type === type) ?? null;

    const status = 'Status' in item ? item.Status : item.status;
    const entityType = 'EntityType' in item ? item.EntityType : item.entityType;
    const entityId = 'EntityID' in item ? item.EntityID : item.entityId;
    const description = 'Description' in item ? item.Description : item.description;
    const rule = 'Rule' in item ? item.Rule : item.rule;
    this.originalRule = rule ?? null;
    const isRegExp = 'IsRegExp' in item ? item.IsRegExp : item.isRegExp;
    const showInDashboard = 'ShowInDashboard' in item ? item.ShowInDashboard : item.showInDashboard;
    const preview = 'Preview' in item ? item.Preview : (this.isGoalView(item) ? item.preview : undefined);

    // Extract customVar fields, with fallback to Preview parsing
    let customVarName = 'CustomVarName' in item ? item.CustomVarName : (this.isGoalView(item) ? item.customVarName : undefined);
    let customVarValue = 'CustomVarValue' in item ? item.CustomVarValue : (this.isGoalView(item) ? item.customVarValue : undefined);
    let customVarOperator = 'CustomVarOperator' in item ? item.CustomVarOperator : (this.isGoalView(item) ? item.customVarOperator : undefined);

    // If customVarName is not available but Preview is, try to extract it from Preview
    if (!customVarName && preview && type === 'customvariablelist') {
      try {
        const parsed = JSON.parse(preview);
        // Preview can be an array or an object
        const previewData = Array.isArray(parsed) ? parsed[0] : parsed;
        // If Name is null or undefined, use '__ANY__' (which displays as "Any")
        customVarName = previewData?.Name ?? '__ANY__';
        customVarOperator = customVarOperator || previewData?.Operator || '';
      } catch {
        // ignore parse errors
      }
    }

    // If customVarName is still not set or is null, default to '__ANY__'
    if (!customVarName || customVarName === 'null') {
      customVarName = '__ANY__';
    }

    this.goalForm.reset({
      id: 'ID' in item ? item.ID : item.id,
      description,
      type,
      rule,
      isRegExp,
      showInDashboard,
      customVarName: customVarName || '',
      customVarValue: '',
      customVarOperator: customVarOperator || '',
      urlValue: '',
      urlOperator: '',
      adsField: '',
      adsOperator: '',
      entityType,
      entityId,
      status,
      ownerEntitySelect: entityId || ''
    });

    this.adsValues = [];
    if (isAdsGoalType(type)) {
      const ads = GoalsFormComponent.parseAdsRule(preview, rule, !!isRegExp);
      this.adsValues = ads.values;
      this.goalForm.patchValue({ adsField: ads.field, adsOperator: ads.operator }, { emitEvent: false });
    }

    // Parse customVarValue as array if editing
    // For custom variable list, prefer parsing Preview over CustomVarValue
    // because Preview contains the original values, while CustomVarValue contains the compiled regex
    if (type === 'customvariablelist' && preview) {
      try {
        const parsed = JSON.parse(preview);
        if (Array.isArray(parsed?.Values)) {
          this.customVarValues = parsed.Values
            .map((val: unknown) => (val ?? '').toString())
            .filter((value: string) => !!value.trim());
        } else {
          // Fallback to existing logic
          this.customVarValues = customVarValue ? [customVarValue] : [];
        }
      } catch {
        // Fallback to existing logic
        if (customVarValue) {
          try {
            const parsed = JSON.parse(customVarValue);
            this.customVarValues = Array.isArray(parsed) ? parsed : [customVarValue];
          } catch {
            this.customVarValues = [customVarValue];
          }
        } else {
          this.customVarValues = [];
        }
      }
    } else if (customVarValue) {
      try {
        const parsed = JSON.parse(customVarValue);
        this.customVarValues = Array.isArray(parsed) ? parsed : [customVarValue];
      } catch {
        this.customVarValues = [customVarValue];
      }
    } else {
      this.customVarValues = [];
    }

    if (type === 'url') {
      this.urlValues = [];
      let operatorFromPreview = '';
      if (preview) {
        try {
          const parsed = JSON.parse(preview);
          if (Array.isArray(parsed?.Values)) {
            this.urlValues = parsed.Values
              .map((val: unknown) => (val ?? '').toString())
              .filter((value: string) => !!value.trim());
          } else if (parsed?.URL) {
            this.urlValues = [parsed.URL.toString()];
          }
          operatorFromPreview = parsed?.Operator || '';
        } catch {
          // ignore parse errors
        }
      }
      if (!this.urlValues.length && rule) {
        // fallback: try to derive from regex? not reliable, so leave empty
      }
      if (operatorFromPreview) {
        this.goalForm.patchValue({ urlOperator: operatorFromPreview }, { emitEvent: false });
      }
    } else {
      this.urlValues = [];
    }
  }

  addCustomVarValue(value: string): boolean {
    const trimmed = value?.trim();
    if (!trimmed) {
      return false;
    }
    if (!this.validateValue(trimmed)) {
      return false;
    }
    if (!this.customVarValues.includes(trimmed)) {
      this.customVarValues.push(trimmed);
    }
    return true;
  }

  removeCustomVarValue(index: number): void {
    this.customVarValues.splice(index, 1);
  }

  addUrlValue(value: string): boolean {
    const trimmed = value?.trim();
    if (!trimmed) {
      return false;
    }
    if (!this.validateValue(trimmed)) {
      return false;
    }
    if (!this.urlValues.includes(trimmed)) {
      this.urlValues.push(trimmed);
    }
    return true;
  }

  removeUrlValue(index: number): void {
    this.urlValues.splice(index, 1);
  }

  addAdsValue(value: string): boolean {
    const trimmed = value?.trim();
    if (!trimmed) {
      return false;
    }
    if (!this.validateValue(trimmed)) {
      return false;
    }
    if (!this.adsValues.includes(trimmed)) {
      this.adsValues.push(trimmed);
    }
    return true;
  }

  removeAdsValue(index: number): void {
    this.adsValues.splice(index, 1);
  }

  /**
   * Ricostruisce campo, operatore e valori di una regola Ads da modificare.
   * La Preview ({Field, Values, Operator}) è la fonte buona: contiene i valori originali.
   * Senza Preview (goal scritto a mano su MySQL) si ripiega sulla Rule, che la procedura
   * legge così: con IsRegExp la chiave è una regexp, altrimenti una lista esatta "a|b".
   */
  private static parseAdsRule(
    preview: string | null | undefined,
    rule: string | null | undefined,
    isRegExp: boolean
  ): { field: string; operator: string; values: string[] } {
    const fields = ADS_GOAL_FIELDS as readonly string[];
    if (preview) {
      try {
        const parsed = JSON.parse(preview);
        if (parsed && fields.includes(parsed.Field)) {
          const values = Array.isArray(parsed.Values)
            ? parsed.Values.map((v: unknown) => (v ?? '').toString()).filter((v: string) => !!v.trim())
            : [];
          return { field: parsed.Field, operator: parsed.Operator || '', values };
        }
      } catch {
        // Preview illeggibile: si prova con la Rule
      }
    }
    if (rule) {
      try {
        const parsed = JSON.parse(rule);
        const field = fields.find(f => typeof parsed?.[f] === 'string' && parsed[f] !== '');
        if (field) {
          const raw: string = parsed[field];
          return isRegExp
            ? { field, operator: 'regexp', values: [raw] }
            : { field, operator: 'equals', values: raw.split('|').filter(v => !!v.trim()) };
        }
      } catch {
        // Rule non JSON: il form resta vuoto e l'utente la riscrive
      }
    }
    return { field: '', operator: '', values: [] };
  }

  /**
   * Con l'operatore "regexp" il valore viene salvato così com'è, quindi un pattern
   * malformato verrebbe scoperto solo dal check BigQuery lato API al salvataggio.
   *
   * Il motore di destinazione è RE2 (quello di BigQuery), non quello di JavaScript, e i due
   * divergono in entrambe le direzioni — verificato interrogando BigQuery costrutto per
   * costrutto con SAFE.REGEXP_CONTAINS:
   *   - RE2 rifiuta e JS accetta: lookahead/lookbehind, backreference, gruppi atomici
   *     → li cerchiamo esplicitamente, perché new RegExp() li lascerebbe passare.
   *   - RE2 accetta e JS rifiuta: flag inline (?i) (?is) (?-s) (?i:…) e named group
   *     (?P<nome>…) → li neutralizziamo prima, perché new RegExp() li boccerebbe a torto.
   * Quel che resta lo valida new RegExp() come controllo di sintassi generico.
   */
  private validateValue(value: string): boolean {
    this.valueError = null;
    if (!this.isRawRegExpOperator) {
      return true;
    }

    const unsupported = GoalsFormComponent.findRe2Unsupported(value);
    if (unsupported) {
      this.valueError = this.translate.instant(unsupported);
      return false;
    }

    try {
      new RegExp(GoalsFormComponent.toJsCompatible(value));
      return true;
    } catch {
      this.valueError = this.translate.instant('goals.errors.invalidRegExp');
      return false;
    }
  }

  /**
   * Cerca i costrutti che RE2 non supporta. Scansione carattere per carattere invece di
   * una regex: serve sapere se siamo dentro una classe [...] o dopo un escape, altrimenti
   * `\\1` (backslash letterale) e `[(?=]` verrebbero segnalati a torto.
   * Restituisce la chiave i18n del messaggio, o null se il pattern è pulito.
   */
  private static findRe2Unsupported(pattern: string): string | null {
    let inClass = false;

    for (let i = 0; i < pattern.length; i++) {
      const char = pattern[i];

      if (char === '\\') {
        const next = pattern[i + 1] ?? '';
        // Le cifre le segnaliamo anche dentro una classe: RE2 rifiuta sia `(a)\1` sia `[\1]`
        // (misurato su BigQuery), mentre JS accetta il secondo come escape ottale.
        if (/[1-9]/.test(next) || (!inClass && next === 'k' && pattern[i + 2] === '<')) {
          return 'goals.errors.re2Backreference';
        }
        i++; // salta il carattere escapato
        continue;
      }

      if (inClass) {
        if (char === ']') {
          inClass = false;
        }
        continue;
      }

      if (char === '[') {
        inClass = true;
        continue;
      }

      if (char === '(' && pattern[i + 1] === '?') {
        const head = pattern.slice(i + 2, i + 4);
        if (head[0] === '=' || head[0] === '!' || (head[0] === '<' && (head[1] === '=' || head[1] === '!'))) {
          return 'goals.errors.re2Lookaround';
        }
        if (head[0] === '>') {
          return 'goals.errors.re2AtomicGroup';
        }
      }
    }

    return null;
  }

  /** Flag inline di RE2: i, m, s, U, anche negati. `x` non esiste in RE2, quindi non va neutralizzato. */
  private static readonly RE2_INLINE_FLAGS = /\(\?(?:[imsU]+(?:-[imsU]+)?|-[imsU]+)([):])/g;

  /** Riscrive i costrutti validi in RE2 ma non in JS, per poterli passare a new RegExp(). */
  private static toJsCompatible(pattern: string): string {
    return pattern
      .replace(/\(\?P</g, '(?<')
      .replace(GoalsFormComponent.RE2_INLINE_FLAGS, (_match, tail: string) => (tail === ':' ? '(?:' : ''));
  }

  async submit(): Promise<void> {
    if (this.goalForm.invalid) {
      this.goalForm.markAllAsTouched();
      return;
    }

    // Additional validation for customVarValues array
    // ("isnotempty" needs no value at all: the rule is the pattern itself)
    const formValue = this.goalForm.getRawValue();
    const noValueNeeded = this.isNoValueOperator;
    if (formValue.type === 'customvariablelist' && !noValueNeeded && this.customVarValues.length === 0) {
      alert(this.translate.instant('goals.errors.noValues'));
      return;
    }
    if (formValue.type === 'url' && !noValueNeeded && this.urlValues.length === 0) {
      alert(this.translate.instant('goals.errors.noValues'));
      return;
    }
    const isAds = isAdsGoalType(formValue.type);
    if (isAds && !noValueNeeded && this.adsValues.length === 0) {
      alert(this.translate.instant('goals.errors.noValues'));
      return;
    }

    // The operator can be switched to "regexp" after the values were added, so re-check them all.
    if (this.isRawRegExpOperator) {
      if (!this.activeValues.every(value => this.validateValue(value))) {
        return;
      }
    }

    // Prepare rule and preview based on type
    let finalRule = formValue.rule;
    let finalPreview = '';
    let isRegExp = formValue.isRegExp;

    if (formValue.type === 'customvariablelist' && formValue.customVarName && (this.customVarValues.length || noValueNeeded) && formValue.customVarOperator) {
      // Convert __ANY__ to null
      const varName = formValue.customVarName === '__ANY__' ? null : formValue.customVarName;

      // Save original values in Preview as JSON
      finalPreview = JSON.stringify({
        Name: varName,
        Values: noValueNeeded ? [] : this.customVarValues,
        Operator: formValue.customVarOperator
      });

      // Build regexp for Rule (combine all values with OR)
      const ruleValues = noValueNeeded ? [''] : this.customVarValues;
      const combinedRegexp = this.buildCombinedRegExp(ruleValues, formValue.customVarOperator);
      finalRule = JSON.stringify([{
        Name: varName,
        Value: combinedRegexp
      }]);
      isRegExp = true;
    } else if (formValue.type === 'url' && this.urlValues.length && formValue.urlOperator) {
      finalPreview = JSON.stringify({
        Values: this.urlValues,
        Operator: formValue.urlOperator
      });
      finalRule = this.buildCombinedRegExp(this.urlValues, formValue.urlOperator);
      isRegExp = true;
    } else if (isAds && formValue.adsField && (this.adsValues.length || noValueNeeded) && formValue.adsOperator) {
      // Rule = {"<Campo>": "<regexp>"}: è il formato letto dal ramo ads_data di df_daily_export_goals.
      finalPreview = JSON.stringify({
        Field: formValue.adsField,
        Values: noValueNeeded ? [] : this.adsValues,
        Operator: formValue.adsOperator
      });
      finalRule = JSON.stringify({
        [formValue.adsField]: this.buildCombinedRegExp(noValueNeeded ? [''] : this.adsValues, formValue.adsOperator)
      });
      isRegExp = true;
    }

    // Auto-set entityType and entityId based on user role
    let entityType: string;
    let entityId: string | null;

    if (this.hasGlobalDataScope && formValue.entityId) {
      entityType = formValue.entityType || 'publisher';
      entityId = formValue.entityId;
    } else {
      const activeDcrId = this.authService.getActiveDcrId();
      if (activeDcrId) {
        entityType = 'publisher';
        entityId = activeDcrId;
      } else if (this.authService.hasGlobalDataAccess()) {
        entityType = 'global';
        entityId = null;
      } else {
        entityType = this.currentUser?.dataAccessLevel || 'DCR';
        entityId = this.currentUser?.entityID || null;
      }
    }

    // Advanced mode lets admins set "show in dashboard" at creation too; the simplified
    // user flow keeps the original behaviour (false on create).
    const showInDashboard = (this.formMode === 'create' && !this.advanced) ? false : formValue.showInDashboard;
    const statusValue = 1; // Always set status to 1 for both create and edit
    // Il gruppo si ricava dall'entityId appena deciso, non da uno stato di sessione a parte:
    // owner e gruppo devono riferirsi alla stessa entity (il backend lo verifica).
    const sourceGroupId = this.hasGlobalDataScope
      ? (this.ownerEntityOptions.find(o => o.value === entityId)?.firstPartyGroupID ?? null)
      : this.authService.getFirstPartyGroupIdFor(entityId);

    if (!sourceGroupId) {
      alert(this.translate.instant('goals.errors.missingSourceGroup'));
      return;
    }

    this.saving = true;
    const payload: Partial<GoalDto> = {
      ID: formValue.id,
      Description: formValue.description,
      Type: formValue.type,
      Rule: finalRule,
      Preview: finalPreview || undefined,
      IsRegExp: isRegExp,
      ShowInDashboard: showInDashboard,
      EntityType: entityType,
      EntityID: entityId || '',
      Status: statusValue,
      SourceGroupID: sourceGroupId
    };

    try {
      let extractingGoalId: string | null = null;
      if (this.formMode === 'edit') {
        await firstValueFrom(this.goalsApi.updateGoal(payload));
        // Solo se la regola è cambiata davvero: rinominare una Custom Rule non deve
        // far ripartire un'estrazione di 90 giorni per riottenere gli stessi dati.
        if (finalRule !== this.originalRule) {
          extractingGoalId = this.triggerExtraction(formValue.id);
        }
      } else {
        const createdGoal = await firstValueFrom(this.goalsApi.createGoal(payload));
        extractingGoalId = this.triggerExtraction(createdGoal?.ID);
      }
      this.saved.emit(extractingGoalId);
      this.openCreate();
      this.urlValues = [];
    } catch (error: any) {
      console.error('[goals] save failed', error);
      alert(error?.message || this.translate.instant('goals.errors.save'));
    } finally {
      this.saving = false;
    }
  }

  cancel(): void {
    if (this.saving) return;
    this.cancelled.emit();
  }

  /**
   * L'estrazione non parte più da sola lato server (vedi settings/goals/post.ts): la lancia
   * il frontend. Volutamente senza await — l'endpoint resta in attesa per tutta la durata del
   * job BigQuery, quindi aspettarlo terrebbe aperto il pannello per minuti. L'utente viene
   * avvisato dal notification center quando l'estrazione finisce o fallisce.
   */
  private triggerExtraction(goalId: string | undefined): string | null {
    if (!goalId) {
      return null;
    }
    this.goalsApi.recalculateGoal(goalId).subscribe({
      error: (err) => console.error('[goals] extraction trigger failed', err)
    });
    return goalId;
  }

  private updateValidators(): void {
    const ruleControl = this.goalForm.get('rule');
    const customVarNameControl = this.goalForm.get('customVarName');
    const customVarOperatorControl = this.goalForm.get('customVarOperator');
    const urlValueControl = this.goalForm.get('urlValue');
    const urlOperatorControl = this.goalForm.get('urlOperator');
    const adsFieldControl = this.goalForm.get('adsField');
    const adsOperatorControl = this.goalForm.get('adsOperator');

    // Clear all validators first
    ruleControl?.clearValidators();
    customVarNameControl?.clearValidators();
    customVarOperatorControl?.clearValidators();
    urlValueControl?.clearValidators();
    urlOperatorControl?.clearValidators();
    adsFieldControl?.clearValidators();
    adsOperatorControl?.clearValidators();

    // Set validators based on type
    if (this.selectedGoalType?.Type === 'customvariablelist') {
      customVarNameControl?.setValidators([Validators.required]);
      customVarOperatorControl?.setValidators([Validators.required]);
    } else if (this.selectedGoalType?.Type === 'url') {
      urlOperatorControl?.setValidators([Validators.required]);
    } else if (this.isAdsType) {
      adsFieldControl?.setValidators([Validators.required]);
      adsOperatorControl?.setValidators([Validators.required]);
    } else if (this.selectedGoalType?.Type) {
      ruleControl?.setValidators([Validators.required]);
    }

    // Update validity
    ruleControl?.updateValueAndValidity();
    customVarNameControl?.updateValueAndValidity();
    customVarOperatorControl?.updateValueAndValidity();
    urlValueControl?.updateValueAndValidity();
    urlOperatorControl?.updateValueAndValidity();
    adsFieldControl?.updateValueAndValidity();
    adsOperatorControl?.updateValueAndValidity();
  }

  private isGoalView(item: GoalDto | GoalView): item is GoalView {
    return (item as GoalView).entityType !== undefined;
  }

  /**
   * Unisce i valori in un'unica alternanza: è la forma in cui la Rule viene salvata.
   * Unico punto di costruzione, così la preview mostrata nel form non può divergere
   * da ciò che finisce davvero su MySQL.
   */
  private buildCombinedRegExp(values: string[], operator: string): string {
    const regexps = values.map(value => this.buildRegExpForOperator(value, operator));
    return regexps.length === 1 ? regexps[0] : `(${regexps.join('|')})`;
  }

  private buildRegExpForOperator(value: string, operator: string): string {
    // "isnotempty" ignores the value: it matches any non-empty string.
    if (operator === 'isnotempty') {
      return '.+';
    }

    // "regexp" means the user typed the pattern themselves: pass it through untouched.
    // Every other operator treats the value as a literal, so metacharacters get escaped.
    if (operator === 'regexp') {
      return value;
    }

    // Escape special regex characters in the value
    const escapedValue = value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    switch (operator) {
      case 'contains':
        return `.*${escapedValue}.*`;
      case 'equals':
        return `^${escapedValue}$`;
      case 'startswith':
        return `^${escapedValue}.*`;
      case 'endswith':
        return `.*${escapedValue}$`;
      default:
        return escapedValue;
    }
  }
}
